import pb from "./pocketbase";
import { isAuthenticated, currentUser } from "../auth/authApi";

const FAV_COLLECTION = "user_favorites";

/** Must match PocketBase `user_favorites.category` select options. */
const ALLOWED_CATEGORIES = new Set(["tools", "resos", "ideaz", "apps"]);

function escapePbFilterValue(value) {
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function normalizeFavoriteCategory(category) {
  const c = String(category ?? "")
    .trim()
    .toLowerCase();
  if (ALLOWED_CATEGORIES.has(c)) return c;
  return "tools";
}

function normalizeFavoriteItemId(itemId) {
  const id = String(itemId ?? "").trim();
  if (!id) {
    throw new Error("Missing item id for favorite");
  }
  if (id.length > 128) {
    throw new Error("Item id exceeds PocketBase max length (128)");
  }
  return id;
}

function favoriteRowFilter(userId, itemId, category) {
  const u = escapePbFilterValue(userId);
  const i = escapePbFilterValue(itemId);
  const c = escapePbFilterValue(category);
  return `user="${u}" && itemId="${i}" && category="${c}"`;
}

export async function toggleFavorite(itemId, category) {
  if (!isAuthenticated()) {
    throw new Error("Not authenticated");
  }
  const user = currentUser();
  const cat = normalizeFavoriteCategory(category);
  const id = normalizeFavoriteItemId(itemId);
  const filter = favoriteRowFilter(user.id, id, cat);
  let existing;
  try {
    existing = await pb.collection(FAV_COLLECTION).getFirstListItem(filter);
  } catch (e) {
    existing = null;
  }
  if (existing && existing.id) {
    await pb.collection(FAV_COLLECTION).delete(existing.id);
    return { status: "unliked", via: "pb" };
  }
  try {
    await pb.collection(FAV_COLLECTION).create({
      user: user.id,
      itemId: id,
      category: cat,
    });
  } catch (err) {
    // Duplicate (race / double click) or "already exists" after a failed lookup: treat as liked.
    if (err?.status === 400) {
      try {
        const row = await pb.collection(FAV_COLLECTION).getFirstListItem(filter);
        if (row?.id) {
          return { status: "liked", via: "pb" };
        }
      } catch (_) {
        /* fall through */
      }
    }
    throw err;
  }
  return { status: "liked", via: "pb" };
}

export async function getMyFavorites() {
  if (!isAuthenticated()) {
    return { myTools: [], myResos: [], myIdeaz: [], myApps: [] };
  }
  const user = currentUser();
  try {
    const list = await pb.collection(FAV_COLLECTION).getFullList({
      filter: `user="${escapePbFilterValue(user.id)}"`,
      sort: "-created",
      perPage: 200,
    });
    const grouped = { myTools: [], myResos: [], myIdeaz: [], myApps: [] };
    list.forEach((fav) => {
      const key = fav.category === "tools" ? "myTools" :
                  fav.category === "resos" ? "myResos" :
                  fav.category === "ideaz" ? "myIdeaz" :
                  fav.category === "apps" ? "myApps" : null;
      if (key) grouped[key].push(fav.itemId);
    });
    return grouped;
  } catch (err) {
    console.warn("getMyFavorites failed", err);
    return { myTools: [], myResos: [], myIdeaz: [], myApps: [] };
  }
}

// Get favorites for a specific user (by PocketBase user ID)
export async function getFavoritesByUser(userId) {
  try {
    const list = await pb.collection(FAV_COLLECTION).getFullList({
      filter: `user="${escapePbFilterValue(userId)}"`,
      sort: "-created",
      perPage: 200,
    });
    const grouped = { myTools: [], myResos: [], myIdeaz: [], myApps: [] };
    list.forEach((fav) => {
      const key = fav.category === "tools" ? "myTools" :
                  fav.category === "resos" ? "myResos" :
                  fav.category === "ideaz" ? "myIdeaz" :
                  fav.category === "apps" ? "myApps" : null;
      if (key) grouped[key].push(fav.itemId);
    });
    return grouped;
  } catch (err) {
    return { myTools: [], myResos: [], myIdeaz: [], myApps: [] };
  }
}